import { useCallback, useRef } from "react";
import { splitIntoChunks } from "../utils/textUtils";

// 우리가 먼저 찾는 목소리. 없으면 아무 영어 목소리나 쓴다.
const PREFERRED_VOICE_NAME_LIST = ["Google UK English Male", "Samantha"];

const isEnglishVoice = (voice) =>
  (voice.lang || "").toLowerCase().startsWith("en");

/**
 * 읽어 줄 영어 목소리를 고른다.
 *
 * **영어가 아닌 목소리로 물러나면 안 된다.** 예전에는 마지막에 `voices[0]` 을 썼는데,
 * 그것은 그 기기에 깔린 첫 목소리라 단말의 언어를 따라간다. 실제로 지문 읽기가
 * 스페인어로 읽혔다. `utterance.lang` 을 "en-US" 로 두어도 소용이 없다.
 * **목소리를 지정하면 브라우저는 `lang` 을 보지 않고 그 목소리의 언어로 읽는다.**
 *
 * 그래서 영어 목소리가 하나도 없으면 `null` 을 준다. 그때는 목소리를 비워 두고
 * `lang` 만 넘겨서 브라우저가 스스로 영어를 찾게 한다.
 */
const pickEnglishVoice = (voiceList) => {
  const englishList = voiceList.filter(isEnglishVoice);
  if (!englishList.length) return null;

  const preferred = PREFERRED_VOICE_NAME_LIST.map((name) =>
    englishList.find((voice) => voice.name === name),
  ).find(Boolean);
  if (preferred) return preferred;

  const guy = englishList.find(
    (voice) => voice.name.includes("Guy") && voice.name.includes("Online"),
  );
  if (guy) return guy;

  return (
    englishList.find((voice) => voice.lang.toLowerCase() === "en-us") ||
    englishList[0]
  );
};

/**
 * 목소리 목록을 받아 온다.
 *
 * **크롬은 목록을 나중에 채운다.** 첫 재생에서 `getVoices()` 가 빈 배열을 주는 일이
 * 잦아, 그대로 고르면 영어 목소리를 놓친다. `voiceschanged` 를 기다리는 것만으로는
 * 부족하다. 안드로이드 웹뷰 중에는 **신호 없이 목록만 슬그머니 채우는 것**이 있어,
 * 짧게 여러 번 다시 물어본다.
 */
const VOICE_LOAD_TIMEOUT_MS = 2000;
const VOICE_POLL_INTERVAL_MS = 100;

const loadVoiceList = () =>
  new Promise((resolve) => {
    const voiceList = speechSynthesis.getVoices();
    if (voiceList.length) {
      resolve(voiceList);
      return;
    }

    let isDone = false;
    const finish = () => {
      if (isDone) return;
      isDone = true;
      speechSynthesis.removeEventListener("voiceschanged", finish);
      clearInterval(pollTimer);
      resolve(speechSynthesis.getVoices());
    };

    const pollTimer = setInterval(() => {
      if (speechSynthesis.getVoices().length) finish();
    }, VOICE_POLL_INTERVAL_MS);

    speechSynthesis.addEventListener("voiceschanged", finish);
    setTimeout(finish, VOICE_LOAD_TIMEOUT_MS);
  });

/**
 * 한 번 고른 목소리는 세션 내내 그대로 쓴다.
 *
 * 재생할 때마다 새로 고르면 **첫 재생과 그다음 재생의 목소리가 달라진다.**
 * 목록이 아직 안 채워진 첫 재생은 목소리 없이(단말 기본 목소리로) 나가고, 다음
 * 재생부터 영어 목소리로 나가서 "매번 소리가 다르고 이상한 발음이 섞인다" 는
 * 항의가 실제로 왔다 (2026-08-26). 그래서
 *
 * - 영어 목소리를 찾으면 붙잡아 두고 계속 쓴다. null 은 붙잡지 않아서, 목록이
 *   늦게 오는 단말은 다음 재생에서 다시 찾는다.
 * - 크롬이 목록을 갈아끼우면(`voiceschanged`) 같은 기준으로 다시 골라 둔다.
 *   옛 목소리 객체를 계속 쓰면 소리가 그냥 안 나는 단말이 있다.
 */
let cachedEnglishVoice = null;

const resolveEnglishVoice = async () => {
  if (cachedEnglishVoice) return cachedEnglishVoice;
  cachedEnglishVoice = pickEnglishVoice(await loadVoiceList());
  return cachedEnglishVoice;
};

if (typeof speechSynthesis !== "undefined") {
  // 첫 재생 전에 미리 목록을 데워 둔다. 크롬은 getVoices() 를 한 번 불러 줘야
  // 목록을 채우기 시작한다.
  speechSynthesis.getVoices();
  speechSynthesis.addEventListener("voiceschanged", () => {
    const picked = pickEnglishVoice(speechSynthesis.getVoices());
    if (picked) cachedEnglishVoice = picked;
  });
}

/**
 * 브라우저 목소리가 자주 틀리게 읽는 낱말을 소리용으로만 바꿔 적는다.
 *
 * `live` 를 "라이브"로 읽어 버린다는 신고가 있었다(2026-09-03 앨리영어). Web Speech
 * 에는 발음을 지정할 방법이 없어서, **읽을 글자만** 바꿔 넣는다. 화면에 보이는
 * 글은 그대로다.
 *
 * **형용사로 쓰인 자리는 건드리지 않는다.** `live concert` 는 "라이브"가 맞다.
 * 앞뒤 낱말로 가른다. 애매하면 바꾸지 않는 쪽으로 둔다.
 */
const LIVE_ADJECTIVE_NEXT_WORD = /^(music|show|shows|concert|concerts|performance|stream|streaming|broadcast|band|event|tv|audience|album|version)\b/i;
const LIVE_ADJECTIVE_PREVIOUS_WORD = /\b(a|the|watch|watching|watched|is|was|are|were)$/i;

export const respellForSpeech = (text) =>
  String(text || "").replace(/\blive\b/gi, (word, offset, whole) => {
    const before = whole.slice(0, offset).trimEnd();
    const after = whole.slice(offset + word.length).trimStart();
    if (LIVE_ADJECTIVE_PREVIOUS_WORD.test(before)) return word;
    if (LIVE_ADJECTIVE_NEXT_WORD.test(after)) return word;
    // 동사 자리. 짧은 i 로 읽히도록 적어 준다.
    return word[0] === "L" ? "Liv" : "liv";
  });

export const useTTS = () => {
  const isSpeakingRef = useRef(false);
  const speakTokenRef = useRef(0);
  const speakingPromiseRef = useRef(Promise.resolve());

  const speak = useCallback(
    async ({ text, setIsSpeaking = null, setIsError = null, rate = null }) => {
      if (!text) return;

      const chunks = splitIntoChunks(respellForSpeech(text));
      const voice = await resolveEnglishVoice();

      const currentToken = speakTokenRef.current + 1;
      speakTokenRef.current = currentToken;

      speechSynthesis.cancel();
      isSpeakingRef.current = true;
      if (setIsSpeaking) setIsSpeaking(true);

      const speakPromise = (async () => {
        try {
          for (let index = 0; index < chunks.length; index++) {
            // 중단 요청 또는 최신 발화가 아니면 재생 중단
            if (
              !isSpeakingRef.current ||
              currentToken !== speakTokenRef.current
            )
              break;

            const chunk = chunks[index];
            await new Promise((resolve, reject) => {
              const utterance = new SpeechSynthesisUtterance(chunk);
              // 영어 목소리를 못 찾았으면 비워 둔다. 그래야 브라우저가 아래 `lang`
              // 을 보고 영어를 찾는다. 아무 목소리나 넣으면 그 언어로 읽는다.
              utterance.voice = voice;
              utterance.lang = "en-US";
              // rate 를 준 곳은 그 값을 그대로 쓴다. 안 준 곳은 예전 그대로 둔다.
              // Samantha 는 같은 값에서도 유난히 빨라 따로 낮춰 왔다.
              const baseRate = voice && voice.name === "Samantha" ? 0.6 : 0.8;
              utterance.rate = rate ? baseRate * rate : baseRate;
              utterance.pitch = 1;

              utterance.onend = () => {
                resolve();
              };

              utterance.onerror = (error) => {
                if (setIsError) setIsError(error);
                reject(error);
              };

              speechSynthesis.speak(utterance);
            });
          }
        } catch (error) {
          console.error("Speech synthesis error:", error);
        } finally {
          if (currentToken === speakTokenRef.current) {
            isSpeakingRef.current = false;
            if (setIsSpeaking) setIsSpeaking(false);
          }
        }
      })();

      speakingPromiseRef.current = speakPromise;
      await speakPromise;
    },
    [],
  );

  const stopSpeaking = useCallback(() => {
    isSpeakingRef.current = false; // 중단 요청
    speakTokenRef.current += 1;
    speechSynthesis.cancel();
  }, []);

  const waitForCurrentSpeech = useCallback(async () => {
    await speakingPromiseRef.current;
  }, []);

  return { speak, stopSpeaking, waitForCurrentSpeech };
};
